// diagramFromCodebase — Multi-sheet diagram-authoring skill som bygger
// ett moduldiagram från repo-strukturen. En sheet per top-level-modul,
// ett block per fil, imports som connectors. Cross-sheet-länkar för
// cross-module-imports.
//
// Skillen är två-stegs:
//   1. plan(ctx) genererar en ExecutionPlan från en injicerad ModuleGraph
//      (tester ger en mock-graph; runtime-loader hämtar via
//      analyzeFileTree på faktiska repo-filer).
//   2. AA1c (codebaseLoader.ts) wrapping som triggers vid runtime.
//
// För skillRegistry-trigger: lyssnar på "moduldiagram", "kartlägg src/",
// "module graph", "rita koden".

import { gridPosition } from '@/lib/aiDiagramTools';
import {
  registerSkill,
  type Skill,
  type SkillContext,
  type ExecutionPlan,
  type SkillStep,
} from '@/lib/aiSkills/skillRegistry';
import {
  type ModuleGraph,
  topLevelModule,
} from '@/lib/aiSkills/codebaseAnalyzer';

const TRIGGER_PATTERNS = [
  /modul(?:-| )?diagram/i,
  /(?:module|modul)(?:-| )graph/i,
  /kartl[aä]gg(?:\s+src)?/i,
  /rita\s+(?:koden|kodbasen)/i,
];

function matchesCodebaseIntent(prompt: string): boolean {
  return TRIGGER_PATTERNS.some(re => re.test(prompt));
}

export interface CodebaseSkillContext extends SkillContext {
  /** Färdig analyserad ModuleGraph. AA1c-loader producerar denna. */
  moduleGraph?: ModuleGraph;
  /** Path-prefix om user vill begränsa skanning (t.ex. "src/lib/aiSkills"). */
  pathPrefix?: string;
  /** Max blocks per sheet. Override default 12. */
  maxBlocksPerSheet?: number;
  /**
   * AB1: Admin-flag. Om false/undefined → plan returnerar error-step
   * utan att någonsin röra moduleGraph (defense-in-depth utöver
   * suggestSkills-filtret i UI-layer).
   */
  isAdmin?: boolean;
}

const SHEET_COLORS = [
  '#1a2e5a', '#4f46e5', '#8b5cf6', '#3ecf8e', '#f97316',
  '#dc2626', '#eab308', '#06b6d4', '#22c55e', '#ec4899',
];

const FILE_TYPE_BY_EXT: Record<string, 'general' | 'condition' | 'webhook'> = {
  '.tsx': 'general',
  '.ts': 'general',
  '.test.ts': 'condition',
  '.test.tsx': 'condition',
  '.spec.ts': 'condition',
  '.cfg.ts': 'webhook',
  '.config.ts': 'webhook',
};

function inferBlockType(path: string): 'general' | 'condition' | 'webhook' {
  for (const [suffix, type] of Object.entries(FILE_TYPE_BY_EXT)) {
    if (path.endsWith(suffix)) return type;
  }
  return 'general';
}

/** Läsbart block-namn från file-path (t.ex. "useDiagramPipeline"). */
function fileLabel(path: string): string {
  const parts = path.split('/');
  const filename = parts[parts.length - 1];
  return filename.replace(/\.(tsx?|jsx?)$/, '');
}

/**
 * Bygg ExecutionPlan för diagramFromCodebase. Antar att ctx.moduleGraph
 * är populerad (loader injicerar). Om null → tom plan med error-step.
 */
export function planDiagramFromCodebase(ctx: CodebaseSkillContext): ExecutionPlan {
  // AB1: defense-in-depth. Även om UI-filter skulle missa → planner
  // ger en tom error-plan och berör ALDRIG moduleGraph om caller inte
  // är admin. Ingen källkod kan läcka genom en felaktig prompt.
  if (!ctx.isAdmin) {
    return {
      skill: 'diagramFromCodebase',
      goal: ctx.userPrompt,
      steps: [{
        index: 0,
        tool: 'plan_diagram',
        args: { goal: ctx.userPrompt },
        description: 'Admin-behörighet krävs — denna skill läser applikationens källkod.',
      }],
      estimatedCost: 0,
    };
  }
  const graph = ctx.moduleGraph;
  if (!graph || graph.modules.length === 0) {
    return {
      skill: 'diagramFromCodebase',
      goal: ctx.userPrompt,
      steps: [{
        index: 0,
        tool: 'plan_diagram',
        args: { goal: ctx.userPrompt },
        description: 'Ingen modulgraf tillgänglig — kör loader först.',
      }],
      estimatedCost: 50,
    };
  }

  const maxPerSheet = ctx.maxBlocksPerSheet ?? 12;
  const steps: SkillStep[] = [];
  let idx = 0;
  const sheetRefForModule = (mod: string) => `sheet-${mod}`;

  // Plan-step (read-only).
  steps.push({
    index: idx++,
    tool: 'plan_diagram',
    args: { goal: ctx.userPrompt, sheet_count: graph.modules.length, style: 'module-graph' },
    description: `Planera ${graph.modules.length} sheets från ${graph.totalFiles} filer`,
  });

  // En sheet per modul + dess filer som blocks.
  for (let mIdx = 0; mIdx < graph.modules.length; mIdx++) {
    const m = graph.modules[mIdx];
    const sheetRef = sheetRefForModule(m.module);
    const color = SHEET_COLORS[mIdx % SHEET_COLORS.length];

    steps.push({
      index: idx++,
      tool: 'create_sheet',
      args: {
        sheet_ref: sheetRef,
        name: `${ordinal(mIdx)} ${m.module}`,
        color,
        description: `${m.files.length} filer i src/${m.module}`,
      },
      description: `Skapa sheet "${m.module}" (${m.files.length} filer)`,
    });

    const truncated = m.files.slice(0, maxPerSheet);
    const blocks = truncated.map((f, i) => {
      const pos = gridPosition(i);
      return {
        ref: `${sheetRef}-${i}`,
        type: inferBlockType(f.path),
        x: pos.x,
        y: pos.y,
        config: {
          name: fileLabel(f.path),
          description: f.path,
        },
        color,
      };
    });
    steps.push({
      index: idx++,
      tool: 'batch_create_blocks',
      args: { sheet_ref: sheetRef, blocks },
      description: `Lägg till ${blocks.length} fil-blocks${m.files.length > maxPerSheet ? ` (truncerat från ${m.files.length})` : ''}`,
    });

    // Within-module-edges (samma sheet).
    const pathToBlockRef = new Map<string, string>();
    truncated.forEach((f, i) => pathToBlockRef.set(f.path, `${sheetRef}-${i}`));
    const sameModuleEdges = graph.edges
      .filter(e => topLevelModule(e.from) === m.module && topLevelModule(e.to) === m.module)
      .filter(e => pathToBlockRef.has(e.from) && pathToBlockRef.has(e.to))
      .map(e => ({
        fromRef: pathToBlockRef.get(e.from)!,
        toRef: pathToBlockRef.get(e.to)!,
      }));
    if (sameModuleEdges.length > 0) {
      steps.push({
        index: idx++,
        tool: 'batch_create_connectors',
        args: { sheet_ref: sheetRef, edges: sameModuleEdges },
        description: `${sameModuleEdges.length} import-edges inom ${m.module}`,
      });
    }
  }

  // Cross-sheet-länkar för cross-module-imports — endast mellan adjacent
  // sheets eftersom det är invariant i workflow.ts. Vi hashar paret
  // (fromMod, toMod) och placerar dem nära varandra i sheet-ordningen
  // för att maximera adjacent-pairs.
  const moduleOrder = new Map<string, number>();
  graph.modules.forEach((m, i) => moduleOrder.set(m.module, i));
  const seen = new Set<string>();
  for (const e of graph.edges) {
    if (!e.crossModule) continue;
    const fromMod = topLevelModule(e.from);
    const toMod = topLevelModule(e.to);
    const fromIdx = moduleOrder.get(fromMod);
    const toIdx = moduleOrder.get(toMod);
    if (fromIdx === undefined || toIdx === undefined) continue;
    if (Math.abs(fromIdx - toIdx) !== 1) continue; // skip non-adjacent
    const key = `${fromMod}->${toMod}`;
    if (seen.has(key)) continue;
    seen.add(key);

    // Använd första filen i respektive modul som anchor.
    const fromSheet = sheetRefForModule(fromMod);
    const toSheet = sheetRefForModule(toMod);
    steps.push({
      index: idx++,
      tool: 'create_cross_sheet_link',
      args: {
        from_sheet_index: fromIdx,
        to_sheet_index: toIdx,
        from_block_ref: `${fromSheet}-0`,
        to_block_ref: `${toSheet}-0`,
        label: `${fromMod}→${toMod}`,
      },
      description: `Cross-sheet: ${fromMod} → ${toMod}`,
    });
  }

  steps.push({
    index: idx++,
    tool: 'validate_diagram',
    args: {},
    description: 'Verifiera invariants på modul-grafen',
  });

  // Cost-estimate skalar med fil-count.
  const blockCount = graph.modules.reduce((n, m) => n + Math.min(m.files.length, maxPerSheet), 0);
  return {
    skill: 'diagramFromCodebase',
    goal: ctx.userPrompt,
    steps,
    estimatedCost: 100 + blockCount * 3 + graph.modules.length * 30,
  };
}

function ordinal(i: number): string {
  return ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩'][i] ?? `(${i + 1})`;
}

const skill: Skill = {
  name: 'diagramFromCodebase' as const,
  description:
    'Bygg ett moduldiagram från repo-strukturen — en sheet per top-level-folder, ett block per fil, imports som connectors. Cross-sheet-länkar för imports mellan moduler.',
  trigger: matchesCodebaseIntent,
  // AB1: admin-only. Skillen läser applikationens källkod via
  // import.meta.glob och skickar fil-namn + imports till AI-gateway.
  // END-USERS får aldrig tillgång till kodbasen via detta flöde.
  requiresAdmin: true,
  // Plan utan moduleGraph fall:s tillbaka till en error-step. Loader
  // injicerar moduleGraph via subset-context-cast.
  plan: (ctx) => planDiagramFromCodebase(ctx as CodebaseSkillContext),
};

registerSkill(skill);

export default skill;

// Helper-export för diagramFromPrompt så den kan delegera när intent
// är moduldiagram (alternativ rutt utan side-effect-import).
export { skill as diagramFromCodebaseSkill };

// Re-export typer för loader.
export type { ModuleGraph, FileNode } from '@/lib/aiSkills/codebaseAnalyzer';
